/**
 * Ticket 侧待办变更 mutations：与 useQuery 配合，关闭/重开做 invalidate，更新做乐观更新。
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useTranslate } from '@tolgee/react';
import { queries } from 'services/queries';
import {
  closeTodo,
  getTodoDetail,
  reopenTodo,
  updateTodo,
} from 'views/Conversation/Sider/ToDoBundle/api';
import type { GetTodoListParams } from 'views/Conversation/Sider/ToDoBundle/api';

export interface UseTicketListParams extends GetTodoListParams {}

/** 关闭待办：成功后 invalidate list */
export function useCloseTodoMutation(listParams: UseTicketListParams | null) {
  const queryClient = useQueryClient();
  const { t } = useTranslate();

  return useMutation({
    mutationFn: (params: { id: string; sendReplyMsg?: boolean }) =>
      closeTodo({ id: params.id, sendReplyMsg: params.sendReplyMsg ?? false }),
    onSuccess: (_, variables, __) => {
      if (listParams) {
        queryClient.invalidateQueries({
          queryKey: queries.tickets.list(listParams).queryKey,
        });
      }
      message.success(variables.sendReplyMsg ? t('reply.已发送') : t('feedback.已关闭'));
    },
    onError: () => {
      message.error(t('operation.saveFailed'));
    },
  });
}

/** 重新打开待办：成功后 invalidate list */
export function useReopenTodoMutation(listParams: UseTicketListParams | null) {
  const queryClient = useQueryClient();
  const { t } = useTranslate();

  return useMutation({
    mutationFn: (params: { id: string }) => reopenTodo(params),
    onSuccess: () => {
      if (listParams) {
        queryClient.invalidateQueries({
          queryKey: queries.tickets.list(listParams).queryKey,
        });
      }
      message.success(t('操作成功'));
    },
    onError: () => {
      message.error(t('operation.saveFailed'));
    },
  });
}

/** 更新待办：乐观更新 list 中对应项，成功后用 getTodoDetail 拉取最新实例写回缓存，不再 refetch 列表。
 * updateTodo 接口不支持局部字段更新，需将缓存中的完整 TodoInstance 与本次更新合并后再提交。 */
export function useUpdateTodoMutation(listParams: UseTicketListParams | null) {
  const queryClient = useQueryClient();
  const { t } = useTranslate();

  return useMutation({
    mutationFn: (data: { id: string } & Partial<Todo.TodoInstance>) => {
      // 合并缓存中的完整实例与本次局部更新，再提交（接口不支持局部更新）
      let payload: Todo.TodoInstance | ({ id: string } & Partial<Todo.TodoInstance>) = data;
      if (listParams) {
        const cached = queryClient.getQueryData<{
          todoInstanceDetails: Todo.TodoInstance[];
        }>(queries.tickets.list(listParams).queryKey);
        const full = cached?.todoInstanceDetails?.find((item) => item.id === data.id);
        if (full) {
          payload = { ...full, ...data };
        }
      }
      return updateTodo({
        ...payload,
        /**
         *  todoStatus 和 status 等价
         * 查询时接口返回 todoStatus
         * 更新时传入 status
         */
        status: payload.todoStatus,
        todoStatus: undefined,
      } as { id: string } & Record<string, any>);
    },
    onMutate: async (variables) => {
      if (!listParams) return;
      const queryKey = queries.tickets.list(listParams).queryKey;
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<{
        todoInstanceDetails: Todo.TodoInstance[];
      }>(queryKey);
      queryClient.setQueryData(queryKey, (old: typeof previous) => {
        if (!old?.todoInstanceDetails) return old;
        return {
          ...old,
          todoInstanceDetails: old.todoInstanceDetails.map((item) =>
            item.id === variables.id ? { ...item, ...variables } : item,
          ),
        };
      });
      return { previous };
    },
    onSuccess: async (_, variables) => {
      if (listParams) {
        const queryKey = queries.tickets.list(listParams).queryKey;
        try {
          const latest = await getTodoDetail(variables.id);
          queryClient.setQueryData<{ todoInstanceDetails: Todo.TodoInstance[] }>(
            queryKey,
            (old) => {
              if (!old?.todoInstanceDetails) return old;
              return {
                ...old,
                todoInstanceDetails: old.todoInstanceDetails.map((item) =>
                  item.id === variables.id ? latest : item,
                ),
              };
            },
          );
        } catch {
          // 拉取详情失败时回退为 invalidate，保证最终一致
          queryClient.invalidateQueries({ queryKey });
        }
      }
      message.success(t('todo.已保存'));
    },
    onError: (_, __, context) => {
      if (context?.previous && listParams) {
        queryClient.setQueryData(queries.tickets.list(listParams).queryKey, context.previous);
      }
      message.error(t('operation.saveFailed'));
    },
  });
}
